import axios from "axios"
import type { QueryClient } from "@tanstack/react-query"
import { depoOku, depoYaz } from "./depo"
import { type DosyaFormu, type ElevatorProject, dosyaFormunuYaz, updateProjectValues } from "@/lib/elevator-projects"
import {
  type ItemResponse, type SessionDetail,
  createSession, deleteResponse, fetchProjectSessions, upsertResponse, type UpsertResponsePayload,
} from "@/lib/checklist-entries"

/**
 * ÇEVRİMDIŞI YAZMA KUYRUĞU — sahada bağlantı yokken yapılan doldurma.
 *
 * Kapsam bilerek DAR: yalnız dosyadaki FORM değerleri, GENEL BİLGİLER
 * değerleri ve KONTROL LİSTESİ madde yanıtları kuyruğa girer — sahada asıl
 * doldurulan bunlar. Süreç
 * başlatma, tamamlama, onay, dosya yükleme gibi işlemler sunucunun kararını
 * gerektirir; bağlantı yokken hata verirler (sessizce "sonra yapılır"
 * sanılmasınlar diye).
 *
 * Aynı hedefe (aynı form örneği / aynı madde) art arda yazılanlar TEK kayıtta
 * birleşir: son yazılan kazanır. Form yazması örneğin bütün değerini, yanıt
 * da maddenin bütün hâlini taşıdığı için ara hâllerin gönderilmesine gerek
 * yok. Çakışma kuralı da bu: bağlantı gelince cihazdaki son hâl sunucudakinin
 * üstüne yazılır.
 *
 * Genel Bilgiler alan alan yazılıyor (gönderilen yalnız değişen anahtarlar):
 * aynı dosyanın kayıtları TEK kayıtta BİRLEŞİR, aynı anahtarda son değer kalır.
 *
 * Kuyruğa giren yazma ÖNBELLEĞE DE işlenir: bağlantı yokken sayfa yenilenince
 * ekran sunucunun eski hâlini değil cihazdaki son hâli göstermeli — yoksa eski
 * değerin üstüne yapılan yeni düzenleme kuyruktaki değeri geri ezerdi.
 *
 * Kuyruk IndexedDB'de durur; sekme kapansa da kaybolmaz, uygulama bir
 * sonraki açılışta (bağlantı varsa) gönderir.
 */

type Kayit =
  | { tur: "form"; anahtar: string; projectId: string; zaman: number; govde: Parameters<typeof dosyaFormunuYaz>[1] }
  | {
      tur: "yanit"
      anahtar: string
      projectId: string
      zaman: number
      checklistId: string
      /** Kuyruğa girerken bilinen oturum; yoksa gönderirken bulunur ya da açılır. */
      sessionId: string | null
      govde: UpsertResponsePayload
    }
  | { tur: "yanit-sil"; anahtar: string; projectId: string; zaman: number; checklistId: string; sessionId: string | null; checklistItemId: string }
  | { tur: "genel"; anahtar: string; projectId: string; zaman: number; degerler: Record<string, unknown> }

/** Kuyrukta duran kayıtların kısa özeti (gösterge için). */
export interface KuyrukDurumu {
  bekleyen: number
  gonderiliyor: boolean
  /** Sunucunun reddettiği (4xx) ve kuyruktan atılan kayıtlar. */
  reddedilen: number
}

const DEPO_ANAHTARI = "kuyruk"
let kayitlar: Kayit[] = []
let yuklendi: Promise<void> | null = null
let gonderiliyor = false
let reddedilen = 0
let durum: KuyrukDurumu = { bekleyen: 0, gonderiliyor: false, reddedilen: 0 }
const dinleyiciler = new Set<() => void>()

function bildir() {
  durum = { bekleyen: kayitlar.length, gonderiliyor, reddedilen }
  dinleyiciler.forEach((d) => d())
}

function yukle() {
  if (!yuklendi) {
    yuklendi = depoOku<Kayit[]>(DEPO_ANAHTARI).then((k) => {
      // Yükleme sürerken eklenenler de kalsın.
      kayitlar = [...(Array.isArray(k) ? k : []), ...kayitlar]
      bildir()
    })
  }
  return yuklendi
}

async function sakla() {
  await depoYaz(DEPO_ANAHTARI, kayitlar)
  bildir()
}

/**
 * Aynı hedefin eski kaydını atar, yenisini sona ekler. Genel Bilgiler kaydı
 * ise eskisiyle BİRLEŞİR (her yazma yalnız değişen alanları taşıyor).
 */
async function ekle(kayit: Kayit) {
  await yukle()
  const eski = kayitlar.find((k) => k.anahtar === kayit.anahtar)
  const yeni: Kayit =
    kayit.tur === "genel" && eski?.tur === "genel" ? { ...kayit, degerler: { ...eski.degerler, ...kayit.degerler } } : kayit
  kayitlar = [...kayitlar.filter((k) => k.anahtar !== kayit.anahtar), yeni]
  await sakla()
}

// --- Önbelleğe işleme --------------------------------------------------------

let qcRef: QueryClient | null = null

/** Kuyruğa giren yazmayı ekranların okuduğu önbelleğe yansıtır. */
function onbellegeIsle(kayit: Kayit, olcumKodu?: (dataRecordFieldId: string) => string | undefined) {
  const qc = qcRef
  if (!qc) return
  if (kayit.tur === "genel") {
    qc.setQueryData<ElevatorProject>(["elevator-project", kayit.projectId], (p) =>
      p ? { ...p, data: { ...((p.data ?? {}) as Record<string, unknown>), ...kayit.degerler } } : p)
    return
  }
  if (kayit.tur === "form") {
    const { auditSetItemId, kopyaNo, data, genelBilgilerden } = kayit.govde
    qc.setQueryData<DosyaFormu[]>(["elevator-project-forms", kayit.projectId], (liste) =>
      liste?.map((f) =>
        f.auditSetItemId === auditSetItemId && f.kopyaNo === kopyaNo
          ? { ...f, ...(data !== undefined ? { data } : {}), ...(genelBilgilerden !== undefined ? { genelBilgilerden } : {}) }
          : f))
    return
  }
  // Oturumu olmayan listenin yanıtı önbelleğe yazılamaz (oturum yok); gönderilince gelir.
  if (!kayit.sessionId) return
  const maddeId = kayit.tur === "yanit" ? kayit.govde.checklistItemId : kayit.checklistItemId
  qc.setQueryData<SessionDetail>(["session-detail", kayit.projectId, kayit.sessionId], (d) => {
    if (!d) return d
    const digerleri = d.responses.filter((r) => r.checklistItemId !== maddeId)
    if (kayit.tur === "yanit-sil") return { ...d, responses: digerleri }
    const eski = d.responses.find((r) => r.checklistItemId === maddeId)
    const g = kayit.govde
    const simdi = new Date().toISOString()
    const yanit: ItemResponse = {
      id: eski?.id ?? `cevrimdisi-${maddeId}`,
      sessionId: kayit.sessionId!,
      checklistItemId: maddeId,
      result: g.result,
      notes: g.notes ?? null,
      nonConformities: (g.nonConformities ?? []).map((text, i) => ({ id: `cevrimdisi-${maddeId}-${i}`, text })),
      measurements: g.measurements
        ? g.measurements.flatMap((m) => {
            const code = olcumKodu?.(m.dataRecordFieldId)
            return code ? [{ id: `cevrimdisi-${m.dataRecordFieldId}`, value: m.value, dataRecordField: { code, name: code, unit: null } }] : []
          })
        : eski?.measurements ?? [],
      photos: eski?.photos ?? [],
      createdAt: eski?.createdAt ?? simdi,
      updatedAt: simdi,
    }
    return { ...d, responses: [...digerleri, yanit] }
  })
}

async function kuyrugaAl(kayit: Kayit, olcumKodu?: (dataRecordFieldId: string) => string | undefined) {
  await ekle(kayit)
  onbellegeIsle(kayit, olcumKodu)
}

/** Yanıt yok = istek sunucuya ulaşmadı (bağlantı yok, sunucu kapalı). */
export function agHatasiMi(e: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true
  return axios.isAxiosError(e) && !e.response
}

export function kuyrukDurumu(): KuyrukDurumu {
  return durum
}

export function kuyruguDinle(dinleyici: () => void) {
  dinleyiciler.add(dinleyici)
  return () => { dinleyiciler.delete(dinleyici) }
}

// --- Yazma yardımcıları (çağıranlar bunları kullanır) -------------------------

/** Sonuç: sunucuya gitti mi, kuyruğa mı alındı. */
export type YazmaSonucu = { kuyrukta: boolean }

export async function formuYaz(projectId: string, govde: Parameters<typeof dosyaFormunuYaz>[1]): Promise<YazmaSonucu> {
  try {
    await dosyaFormunuYaz(projectId, govde)
    return { kuyrukta: false }
  } catch (e) {
    if (!agHatasiMi(e)) throw e
    await kuyrugaAl({
      tur: "form",
      anahtar: `form:${projectId}:${govde.auditSetItemId}:${govde.kopyaNo}`,
      projectId,
      zaman: Date.now(),
      govde,
    })
    return { kuyrukta: true }
  }
}

export async function yanitiYaz(
  projectId: string,
  checklistId: string,
  sessionId: string | null,
  govde: UpsertResponsePayload,
  /** Ölçüm alanının kodu — kuyruğa girerse önbellekteki yanıtta ölçüm görünsün. */
  olcumKodu?: (dataRecordFieldId: string) => string | undefined,
): Promise<YazmaSonucu> {
  try {
    if (!sessionId) throw new CevrimdisiOturum()
    await upsertResponse(projectId, sessionId, govde)
    return { kuyrukta: false }
  } catch (e) {
    if (!(e instanceof CevrimdisiOturum) && !agHatasiMi(e)) throw e
    await kuyrugaAl({
      tur: "yanit",
      anahtar: `yanit:${projectId}:${checklistId}:${govde.checklistItemId}`,
      projectId,
      zaman: Date.now(),
      checklistId,
      sessionId,
      govde,
    }, olcumKodu)
    return { kuyrukta: true }
  }
}

export async function yanitiSil(
  projectId: string,
  checklistId: string,
  sessionId: string | null,
  checklistItemId: string,
): Promise<YazmaSonucu> {
  try {
    if (!sessionId) throw new CevrimdisiOturum()
    await deleteResponse(projectId, sessionId, checklistItemId)
    return { kuyrukta: false }
  } catch (e) {
    if (!(e instanceof CevrimdisiOturum) && !agHatasiMi(e)) throw e
    await kuyrugaAl({
      tur: "yanit-sil",
      anahtar: `yanit:${projectId}:${checklistId}:${checklistItemId}`,
      projectId,
      zaman: Date.now(),
      checklistId,
      sessionId,
      checklistItemId,
    })
    return { kuyrukta: true }
  }
}

/**
 * Genel Bilgiler değerleri (yalnız değişen üst düzey anahtarlar). Bağlantı
 * varken sunucunun yanıtı döner — "Personel" alanının açtığı görevler orada.
 */
export async function genelDegerleriYaz(
  projectId: string,
  degerler: Record<string, unknown>,
): Promise<{ kuyrukta: true } | { kuyrukta: false; sonuc: Awaited<ReturnType<typeof updateProjectValues>> }> {
  try {
    return { kuyrukta: false, sonuc: await updateProjectValues(projectId, degerler) }
  } catch (e) {
    if (!agHatasiMi(e)) throw e
    await kuyrugaAl({ tur: "genel", anahtar: `genel:${projectId}`, projectId, zaman: Date.now(), degerler })
    return { kuyrukta: true }
  }
}

/** Oturumu henüz açılmamış listenin yanıtı — bağlantı yokken oturum açılamaz. */
class CevrimdisiOturum extends Error {}

// --- Gönderme --------------------------------------------------------------

/**
 * Kuyruğu sırayla gönderir. Ağ hatasında durur (sonra yeniden denenir);
 * sunucunun reddettiği kayıt (4xx) atılır ve sayılır — aksi hâlde tek bozuk
 * kayıt kuyruğun geri kalanını sonsuza dek bekletirdi. 5xx'te de durulur.
 */
export async function kuyruguGonder(qc?: QueryClient): Promise<void> {
  await yukle()
  if (gonderiliyor || kayitlar.length === 0) return
  gonderiliyor = true
  bildir()
  const oturumlar = new Map<string, string>()
  const dokunulan = new Set<string>()
  try {
    while (kayitlar.length) {
      const k = kayitlar[0]
      try {
        await gonderBir(k, oturumlar)
        dokunulan.add(k.projectId)
      } catch (e) {
        const durumKodu = axios.isAxiosError(e) ? e.response?.status : undefined
        if (agHatasiMi(e) || durumKodu === undefined || durumKodu >= 500) break
        reddedilen++
      }
      // Gönderilen ya da reddedilen kayıt düşer — arada yenisi eklendiyse o kalır.
      kayitlar = kayitlar.filter((x) => x !== k)
      await sakla()
    }
  } finally {
    gonderiliyor = false
    bildir()
    if (qc) {
      for (const pid of dokunulan) {
        void qc.invalidateQueries({ queryKey: ["elevator-project-forms", pid] })
        void qc.invalidateQueries({ queryKey: ["elevator-project-sessions", pid] })
        void qc.invalidateQueries({ queryKey: ["session-detail", pid] })
        void qc.invalidateQueries({ queryKey: ["elevator-project", pid] })
        // Genel Bilgiler'deki "Personel" alanı gönderilince görev açılmış olabilir.
        void qc.invalidateQueries({ queryKey: ["personnel-tasks"] })
      }
    }
  }
}

async function gonderBir(k: Kayit, oturumlar: Map<string, string>) {
  if (k.tur === "form") {
    await dosyaFormunuYaz(k.projectId, k.govde)
    return
  }
  if (k.tur === "genel") {
    await updateProjectValues(k.projectId, k.degerler)
    return
  }
  const sid = k.sessionId ?? (await oturumBul(k.projectId, k.checklistId, oturumlar))
  if (k.tur === "yanit") await upsertResponse(k.projectId, sid, k.govde)
  else await deleteResponse(k.projectId, sid, k.checklistItemId)
}

/** Listenin geçerli (en yeni) oturumu; yoksa açılır. Gönderim boyunca bir kez. */
async function oturumBul(projectId: string, checklistId: string, oturumlar: Map<string, string>) {
  const anahtar = `${projectId}:${checklistId}`
  const bilinen = oturumlar.get(anahtar)
  if (bilinen) return bilinen
  const { data } = await fetchProjectSessions(projectId, { pageSize: 100 })
  const son = data
    .filter((s) => s.checklistId === checklistId)
    .sort((a, b) => a.startedAt.localeCompare(b.startedAt))
    .at(-1)
  const id = son?.id ?? (await createSession(projectId, { checklistId })).id
  oturumlar.set(anahtar, id)
  return id
}

/**
 * Uygulama açılışında bir kez: kuyruğu yükler, bağlantı gelince ve sekme
 * öne geldiğinde gönderir.
 */
export function kuyruguBaslat(qc: QueryClient) {
  qcRef = qc
  const dene = () => { if (navigator.onLine !== false) void kuyruguGonder(qc) }
  void yukle().then(dene)
  window.addEventListener("online", dene)
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") dene() })
  // Sunucu kapalıyken navigator.onLine yine true olabilir: arada bir dene.
  window.setInterval(() => { if (kayitlar.length) dene() }, 30_000)
}
